import {
  ArrowRight,
  Eye,
  FileKey,
  Fingerprint,
  LockKeyhole,
  ScrollText,
  ShieldCheck,
} from "lucide-react"
import type { Metadata } from "next"
import Link from "next/link"

export const metadata: Metadata = {
  title: "Security",
  description:
    "Resolto's designed security boundaries: read-only first, tenant isolation, explicit authority, and complete audit history.",
}

const controls = [
  {
    title: "Tenant isolation",
    text: "Organisation scope is resolved from the authenticated session and enforced in domain policy, queues, storage, and PostgreSQL RLS.",
    icon: Fingerprint,
  },
  {
    title: "Least-privilege credentials",
    text: "Connector credentials are write-only after entry, envelope-encrypted, versioned, rotated, and excluded from model input and telemetry.",
    icon: FileKey,
  },
  {
    title: "Read-only first",
    text: "Investigation uses typed approved operations with row, byte, time, recursion, host, and capability limits.",
    icon: Eye,
  },
  {
    title: "Human authority",
    text: "Every write shows exact scope, current state, proposed state, risk, approver, preconditions, postconditions, and rollback information.",
    icon: ShieldCheck,
  },
  {
    title: "Untrusted-content isolation",
    text: "Customer data, tickets, logs, documents, and code are evidence—not instructions—and cannot become executable SQL, shell, JavaScript, or URLs.",
    icon: LockKeyhole,
  },
  {
    title: "Tamper-evident audit",
    text: "Append-only organisation streams capture actor, permission decision, target, outcome, request IDs, and a previous-event hash.",
    icon: ScrollText,
  },
]

export default function SecurityPage() {
  return (
    <main id="main-content">
      <section className="marketing-page-hero">
        <div className="marketing-container">
          <p className="section-kicker">Security and governance</p>
          <h1>Control is a product feature, not a promise beneath the interface.</h1>
          <p>
            Resolto is designed around explicit access, bounded tools, visible uncertainty, human
            approval, verified outcomes, and a complete history of what happened.
          </p>
        </div>
      </section>
      <section className="marketing-section security-section">
        <div className="marketing-container security-list">
          {controls.map((control) => {
            const Icon = control.icon
            return (
              <article key={control.title}>
                <Icon aria-hidden="true" />
                <div>
                  <h2>{control.title}</h2>
                  <p>{control.text}</p>
                </div>
              </article>
            )
          })}
        </div>
      </section>
      <section className="marketing-section assurance-section">
        <div className="marketing-container assurance-panel">
          <div>
            <h2>Boundaries that remain unavailable</h2>
            <p>
              Resolto does not offer arbitrary database writes, arbitrary HTTP or shell, bulk or
              destructive action execution, access-right changes, direct merge, or deployment.
            </p>
          </div>
          <div>
            <h2>Compliance status</h2>
            <p>
              This product does not claim an unverified certification or legal compliance status.
              Security documentation and controls must be independently reviewed before a public
              production launch.
            </p>
          </div>
        </div>
      </section>
      <section className="marketing-section compact-cta-section">
        <div className="marketing-container compact-cta">
          <div>
            <h2>Review the actual control surface.</h2>
            <p>Open the demo approval and inspect target, authority, evidence, and verification.</p>
          </div>
          <Link className="button button-primary" href="/app/approvals">
            View approval controls
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  )
}
